import { SceneFormat } from '@mkkellogg/gaussian-splats-3d';
import * as THREE from 'three';
// 创建一个传入xyz旋转角度的函数，返回一个欧拉矩阵
interface ScreenResolution {
  width: number;
  height: number;
  pixelRatio: number;
  physicalWidth: number;
  physicalHeight: number;
}

interface ScreenOrientation {
  orientation: '横屏' | '竖屏';
  angle: number;
  isLandscape: boolean;
  isPortrait: boolean;
}

export function getRotationMatrix(rotate: number[] | undefined): number[] | undefined {
  if (rotate?.length !== 3) return;
  const [x, y, z] = rotate;
  const euler = new THREE.Euler(
    THREE.MathUtils.degToRad(x),
    THREE.MathUtils.degToRad(y),
    THREE.MathUtils.degToRad(z),
    'XYZ',
  ); // 45度，90度，0度，XYZ顺序
  // 创建一个四元数对象
  const quaternion = new THREE.Quaternion();
  // 使用 setFromEuler() 方法将欧拉角转换为四元数
  quaternion.setFromEuler(euler);
  return [quaternion.x, quaternion.y, quaternion.z, quaternion.w];
}
// 传入一个url，或者url的后缀是否含有 .splat .ply .ksplat 并返回
export function getGSFileExt(path: string | undefined): number | null {
  if (typeof path !== 'string') return null;
  const lower = path.toLowerCase();
  if (lower.endsWith('.splat')) return SceneFormat.Splat;
  if (lower.endsWith('.ply')) return SceneFormat.Ply;
  if (lower.endsWith('.ksplat')) return SceneFormat.KSplat;
  return null;
}

// 获取网页的宽高
export function getWindowSize(): { width: number; height: number } {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

/**
 * 检测设备是横屏还是竖屏
 * @returns {Object} 包含orientation(横屏/竖屏)和angle(旋转角度)的对象
 */
export function getScreenOrientation(): ScreenOrientation {
  let orientation: string;
  let angle: number;
  // 获取屏幕方向信息
  if (window.screen?.orientation) {
    // 现代浏览器 API
    orientation = window.screen.orientation.type;
    angle = window.screen.orientation.angle;
  } else if (window.orientation !== undefined) {
    // 旧版 iOS Safari 和一些旧浏览器
    angle = window.orientation;
    orientation = angle === 0 || angle === 180 ? 'portrait' : 'landscape';
  } else {
    // 回退方案：通过窗口尺寸判断
    orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
    angle = orientation === 'landscape' ? 90 : 0;
  }

  return {
    orientation: orientation.includes('landscape') ? '横屏' : '竖屏',
    angle,
    isLandscape: orientation.includes('landscape'),
    isPortrait: orientation.includes('portrait'),
  };
}

// 获取某个元素的宽高
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getElementSize(element: HTMLElement): { width: number; height: number } {
  // return {
  //   width: element.clientWidth,
  //   height: element.clientHeight,
  // };
  if (!element || element === document.body || element === document.documentElement) {
    return { width: window.innerWidth, height: window.innerHeight };
  }
  const rect = element.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

export function isWeixin(): boolean {
  console.log('isWeixin===', navigator.userAgent);
  return /micromessenger/i.test(navigator.userAgent);
}

export function loadWxJsSdk(): Promise<any> {
  return new Promise((resolve, reject) => {
    if (window.wx) return resolve(window.wx);
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = './jweixin.js';
    script.async = true;
    script.onload = () => resolve(window.wx);
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
}

/**
 * 将角度转换为弧度，用于GLTF的rotation值
 * @param {Array} degrees - 角度数组 [x, y, z]，例如 [0, 180, 0]
 * @returns {Array} 弧度数组 [x, y, z]，例如 [0, Math.PI, 0]
 */
export function degreesToRadians(degrees: number[]): number[] {
  if (!Array.isArray(degrees) || degrees.length !== 3) {
    console.warn('degreesToRadians: 参数必须是包含3个元素的数组');
    return [0, 0, 0];
  }
  return degrees.map((degree) => THREE.MathUtils.degToRad(degree));
}

export function getScreenResolution(): ScreenResolution {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const pixelRatio = window.devicePixelRatio || 1;

  return {
    width,
    height,
    pixelRatio,
    physicalWidth: Math.round(width * pixelRatio),
    physicalHeight: Math.round(height * pixelRatio),
  };
}
export function checkIsSafari() {
  const ua = navigator.userAgent;
  return (
    /Safari/.test(ua) &&
    !/Chrome/.test(ua) &&
    !/CriOS/.test(ua) &&
    !/Edge/.test(ua) &&
    !/Edg/.test(ua) &&
    !/OPR/.test(ua)
  );
}
